import { ArrowBack } from '@mui/icons-material';
import { Box, Button, CircularProgress, FormControlLabel, IconButton, Paper, Slider, Switch, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import util from '../util';
import TokensPanel from '../git/TokensPanel';
import McpPanel from '../mcp/McpPanel';

const SettingsPage = () => {
  const navigate = useNavigate();

  const [concurrence, setConcurrence] = useState(1);
  const [uploadChunkConcurrence, setUploadChunkConcurrence] = useState(1);
  const [thumbnail, setThumbnail] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [concurrenceLoading, setConcurrenceLoading] = useState(false);
  const [uploadChunkConcurrenceLoading, setUploadChunkConcurrenceLoading] = useState(false);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [concurrenceError, setConcurrenceError] = useState('');
  const [uploadChunkConcurrenceError, setUploadChunkConcurrenceError] = useState('');
  const [thumbnailError, setThumbnailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (typeof user.concurrence === 'number') {
        setConcurrence(user.concurrence);
      }
      if (typeof user.uploadChunkConcurrence === 'number') {
        setUploadChunkConcurrence(user.uploadChunkConcurrence);
      }
      if (typeof user.thumbnail === 'boolean') {
        setThumbnail(user.thumbnail);
      }
    } catch { /* ignore */ }
  }, []);

  const handleConcurrenceChange = async () => {
    setConcurrenceError('');
    setConcurrenceLoading(true);
    try {
      await util.postRequest('/api/auth/setConcurrence', { concurrence });
      let user = JSON.parse(localStorage.getItem('user') || '{}');
      user.concurrence = concurrence;
      localStorage.setItem('user', JSON.stringify(user));
    } catch (e) {
      setConcurrenceError(e.message || 'Failed to update concurrence');
    } finally {
      setConcurrenceLoading(false);
    }
  };

  const handleUploadChunkConcurrenceChange = async () => {
    setUploadChunkConcurrenceError('');
    setUploadChunkConcurrenceLoading(true);
    try {
      await util.postRequest('/api/auth/setUploadChunkConcurrence', { uploadChunkConcurrence });
      let user = JSON.parse(localStorage.getItem('user') || '{}');
      user.uploadChunkConcurrence = uploadChunkConcurrence;
      localStorage.setItem('user', JSON.stringify(user));
    } catch (e) {
      setUploadChunkConcurrenceError(e.message || 'Failed to update upload chunk concurrence');
    } finally {
      setUploadChunkConcurrenceLoading(false);
    }
  };

  const handleThumbnailChange = async (checked) => {
    setThumbnailError('');
    setThumbnailLoading(true);
    try {
      await util.postRequest('/api/auth/setThumbnail', { thumbnail: checked });
      setThumbnail(checked);
      let user = JSON.parse(localStorage.getItem('user') || '{}');
      user.thumbnail = checked;
      localStorage.setItem('user', JSON.stringify(user));
    } catch (e) {
      if (e.message?.toLowerCase().includes('already')) {
        setThumbnailError('Value is already set to this');
      } else {
        setThumbnailError(e.message || 'Failed to update thumbnails setting');
      }
    } finally {
      setThumbnailLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    if (!oldPassword) {
      setPasswordError('Old password is required');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      await util.postRequest('/api/auth/resetPassword', { oldPassword, newPassword });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      if (e.message?.includes('password')) {
        setPasswordError('Current password is incorrect');
      } else {
        setPasswordError(e.message || 'Failed to change password');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 2, p: 2, backgroundColor: '#151515', borderRadius: 1, minHeight: 'calc(100vh - 110px)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/contentManager')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" sx={{ ml: 1 }}>Settings</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 500 }}>

        {/* Concurrence */}
        <Paper sx={{ backgroundColor: '#1d1d1d', p: 2.5 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Concurrence</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Slider
              value={concurrence}
              min={1}
              max={10}
              step={1}
              marks
              onChange={(e, value) => setConcurrence(value)}
              sx={{ flex: 1 }}
            />
            <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>{concurrence}</Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={handleConcurrenceChange}
            disabled={concurrenceLoading}
            startIcon={concurrenceLoading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Save Concurrence
          </Button>
          {concurrenceError && (
            <Typography sx={{ mt: 1, fontSize: 12, color: 'error.main' }}>{concurrenceError}</Typography>
          )}
        </Paper>

        {/* Upload Chunk Concurrence */}
        <Paper sx={{ backgroundColor: '#1d1d1d', p: 2.5 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Upload Chunk Concurrence</Typography>
          <Typography sx={{ mb: 1, fontSize: 13, opacity: 0.7 }}>How many chunks are uploaded at once per file.</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Slider
              value={uploadChunkConcurrence}
              min={1}
              max={16}
              step={1}
              marks
              onChange={(e, value) => setUploadChunkConcurrence(value)}
              sx={{ flex: 1 }}
            />
            <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>{uploadChunkConcurrence}</Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={handleUploadChunkConcurrenceChange}
            disabled={uploadChunkConcurrenceLoading}
            startIcon={uploadChunkConcurrenceLoading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Save Upload Chunk Concurrence
          </Button>
          {uploadChunkConcurrenceError && (
            <Typography sx={{ mt: 1, fontSize: 12, color: 'error.main' }}>{uploadChunkConcurrenceError}</Typography>
          )}
        </Paper>

        {/* Thumbnails */}
        <Paper sx={{ backgroundColor: '#1d1d1d', p: 2.5 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Thumbnails</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={thumbnail}
                onChange={(e) => handleThumbnailChange(e.target.checked)}
                disabled={thumbnailLoading}
              />
            }
            label="Enable thumbnails"
          />
          {thumbnailLoading && (
            <Box sx={{ mt: 0.5 }}>
              <CircularProgress size={16} />
            </Box>
          )}
          {thumbnailError && (
            <Typography sx={{ mt: 0.5, fontSize: 12, color: 'error.main' }}>{thumbnailError}</Typography>
          )}
        </Paper>

        {/* Change Password */}
        <Paper sx={{ backgroundColor: '#1d1d1d', p: 2.5 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Change Password</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1.5 }}>
            <TextField
              label="Old Password"
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              size="small"
              fullWidth
            />
          </Box>
          <Button
            variant="outlined"
            onClick={handlePasswordChange}
            disabled={passwordLoading}
            startIcon={passwordLoading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Change Password
          </Button>
          {passwordError && (
            <Typography sx={{ mt: 1, fontSize: 12, color: 'error.main' }}>{passwordError}</Typography>
          )}
        </Paper>

        {/* Git / LFS Personal Access Tokens */}
        <TokensPanel />

      </Box>

      <Box sx={{ mt: 2, maxWidth: 1000 }}>
        <McpPanel />
      </Box>
    </Box>
  );
};

export default SettingsPage;
